import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
  StaffStudentService,
  StudentProfile,
  StudentAttendanceInfo,
  StudentSibling,
  StudentClassTeacher,
  StudentSubjectTeacher,
  StudentFeesDetails,
  TermFee,
  TypeFee,
  OtherFeeTerm,
  AttendanceCalendarEntry,
  AttendanceDayStatus,
  PartialAttendancePeriod,
  UploadedDocument,
} from '../services/staff-student.service';
import { StaffSessionService } from '../services/staff-session.service';
import { StaffClassService } from '../services/staff-class.service';

type FeeSourceTag = { isTerm?: true } | { isType?: true } | { isOther?: true };
type FeeItem = (TermFee | TypeFee | OtherFeeTerm) & FeeSourceTag & { [key: string]: any };
type FeeStatus = 'Paid' | 'Due' | 'Overdue' | 'Pending';

interface CalendarDayTile {
  date: Date;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isFuture: boolean;
  status: AttendanceDayStatus | null;
}

@Component({
  selector: 'app-staff-student-detail',
  templateUrl: './staff-student-detail.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-student-detail.component.css']
})
export class StaffStudentDetailComponent implements OnInit {
  studentId: string;
  activeTab: 'info' | 'fees' | 'attendance' | 'documents' = 'info';

  allTabs = [
    { key: 'info', label: 'personalInfo', icon: 'ri-user-line' },
    { key: 'fees', label: 'fees', icon: 'ri-money-dollar-circle-line' },
    { key: 'attendance', label: 'attendanceLabel', icon: 'ri-checkbox-line' },
    { key: 'documents', label: 'documents', icon: 'ri-file-text-line' },
  ];

  get isDocumentHubEnabled(): boolean {
    const info = this.staffSession.getSchoolAndStaffInfo();
    if (!info) { return false; }
    return !!(info.isSuperAdmin || info.isFeeDefaulterAdmin || info.isFinanceAdmin || info.isClassTeacher);
  }

  get tabs() {
    return this.allTabs.filter(t => {
      if (t.key === 'documents' && !this.isDocumentHubEnabled) { return false; }
      if (t.key === 'attendance' && !this.staffSession.isFeatureEnabled('ATTENDANCE')) { return false; }
      return true;
    });
  }

  loading = true;
  loadError = false;

  profile: StudentProfile | null = null;
  attendanceInfo: StudentAttendanceInfo | null = null;
  siblings: StudentSibling[] = [];
  classTeachers: StudentClassTeacher[] = [];
  subjectTeachers: StudentSubjectTeacher[] = [];
  feesDetails: StudentFeesDetails | null = null;

  // Fees tab (FeesList) — full itemized breakdown, separate from the
  // FeesInfo summary card above. Fetched lazily the first time the Fees
  // tab is opened.
  feesLoading = false;
  feesLoadError = false;
  feesLoaded = false;
  feeItems: FeeItem[] = [];
  otherFeeItems: FeeItem[] = [];
  feesTotalAmount = 0;
  feesTotalPaid = 0;
  feesTotalDiscount = 0;
  currencySymbol = '₹';

  // Attendance tab (StudentAttendance) — monthly calendar with per-day
  // status coloring, plus a period-wise drill-down for PARTIAL days.
  attendanceCalendarLoading = false;
  attendanceCalendarLoaded = false;
  attendanceMonthPercent = 0;
  attendanceEntries: AttendanceCalendarEntry[] = [];
  calendarMonth = new Date();
  calendarWeeks: CalendarDayTile[][] = [];
  selectedDay: CalendarDayTile | null = null;
  partialLoading = false;
  partialPeriods: PartialAttendancePeriod[] = [];

  // Documents tab (DocumentsHub) — fetched lazily and independently managed,
  // not part of the /student/info payload.
  documentsLoading = false;
  documentsLoaded = false;
  documentsLoadError = false;
  allDocuments: UploadedDocument[] = [];
  documents: UploadedDocument[] = [];
  documentSearchQuery = '';

  showDocumentPreview = false;
  previewDocument: UploadedDocument | null = null;
  previewSafeUrl: SafeResourceUrl | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private staffStudentService: StaffStudentService,
    private staffClassService: StaffClassService,
    private staffSession: StaffSessionService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.studentId = this.route.snapshot.paramMap.get('id') || '';
    this.currencySymbol = this.staffSession.getSchoolAndStaffInfo()?.currencySymbol || this.currencySymbol;
    this.loadStudentInfo();
  }

  async loadStudentInfo() {
    const staffDetails = this.staffSession.getStaffDetails();
    if (!staffDetails || !this.studentId) {
      this.loading = false;
      this.loadError = true;
      return;
    }

    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(
        this.staffStudentService.getStudentInfo(staffDetails.staffId, this.studentId)
      );
      this.profile = response.profile || null;
      this.attendanceInfo = response.attendanceInfo || null;
      this.siblings = response.siblings || [];
      this.classTeachers = response.classTeachers || [];
      this.subjectTeachers = Object.values(response.staffSubjectInfo || {}).flat();
      this.feesDetails = response.feesDetails || null;
    } catch (error) {
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  get studentInitials(): string {
    return this.profile ? this.staffSession.getInitials(this.profile.studentName) : '';
  }

  get classLabel(): string {
    if (!this.profile) { return ''; }
    return `${this.profile.classNumber} - ${this.profile.classDivision}`;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) { return '—'; }
    const date = new Date(value);
    if (isNaN(date.getTime())) { return value; }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  monthlyAttendancePercent(): number {
    if (!this.attendanceInfo || !this.attendanceInfo.MonthDays) { return 0; }
    return Math.round((this.attendanceInfo.MonthDaysPresent / this.attendanceInfo.MonthDays) * 100);
  }

  academicAttendancePercent(): number {
    if (!this.attendanceInfo || !this.attendanceInfo.AcademicDays) { return 0; }
    return Math.round((this.attendanceInfo.AcademicDaysPresent / this.attendanceInfo.AcademicDays) * 100);
  }

  todayAttendanceChipClass(): string {
    switch (this.attendanceInfo?.TodayAttendance) {
      case 'PRESENT': return 'st-chip-green';
      case 'ABSENT': return 'st-chip-red';
      case 'LEAVE': return 'st-chip-blue';
      default: return 'st-chip-grey';
    }
  }

  setTab(tab: any) {
    this.activeTab = tab;
    if (tab === 'fees' && !this.feesLoaded && !this.feesLoading) {
      this.loadFeesList();
    }
    if (tab === 'attendance' && !this.attendanceCalendarLoaded && !this.attendanceCalendarLoading) {
      this.loadAttendanceCalendar(this.calendarMonth);
    }
    if (tab === 'documents' && !this.documentsLoaded && !this.documentsLoading) {
      this.loadDocuments();
    }
  }

  // ---------- Fees tab (FeesList) ----------
  async loadFeesList() {
    const staffDetails = this.staffSession.getStaffDetails();
    const classData = this.staffSession.getClassData() as any;
    const classId = this.profile?.['classId'] ?? classData?.classId;
    if (!staffDetails || !classId || !this.studentId) {
      this.feesLoadError = true;
      return;
    }

    this.feesLoading = true;
    this.feesLoadError = false;
    try {
      const response = await firstValueFrom(
        this.staffStudentService.getFeesStudentWise(classId, this.studentId, staffDetails.staffId)
      );
      const termFees: FeeItem[] = (response.termFees || []).map(f => ({ ...f, isTerm: true as const }));
      const typeFees: FeeItem[] = (response.typeFees || []).map(f => ({ ...f, isType: true as const }));
      const otherFeeTerms: FeeItem[] = (response.otherFeeTerms || []).map(f => ({ ...f, isOther: true as const }));

      this.feeItems = this.sortFeesUnpaidFirst([...termFees, ...typeFees]);
      this.otherFeeItems = this.sortFeesUnpaidFirst(otherFeeTerms);

      let totalAmount = 0;
      let totalPaid = 0;
      let totalDiscount = 0;
      [...this.feeItems, ...this.otherFeeItems].forEach(fee => {
        totalAmount += this.getTotalFees(fee);
        totalPaid += parseFloat(String(fee.Paid)) || 0;
        totalDiscount += parseFloat(String(this.getDiscount(fee))) || 0;
      });
      this.feesTotalAmount = totalAmount;
      this.feesTotalPaid = totalPaid;
      this.feesTotalDiscount = totalDiscount;
      this.feesLoaded = true;
    } catch (error) {
      this.feesLoadError = true;
    } finally {
      this.feesLoading = false;
    }
  }

  get isFeesDataVisible(): boolean {
    return this.feeItems.length > 0 || this.otherFeeItems.length > 0;
  }

  private sortFeesUnpaidFirst(items: FeeItem[]): FeeItem[] {
    const unpaid = items.filter(f => f.Balance > 0);
    const paid = items.filter(f => f.Balance <= 0);
    unpaid.sort((a, b) => (this.getDueDate(a) || '').localeCompare(this.getDueDate(b) || ''));
    return [...unpaid, ...paid];
  }

  getFeeName(fee: FeeItem): string {
    const raw = ('isTerm' in fee && fee.isTerm) ? (fee as TermFee).TermName
      : ((fee as TypeFee | OtherFeeTerm).Name);
    const fallback = ('isTerm' in fee && fee.isTerm) ? this.translate.instant('termFeeSingular')
      : ('isType' in fee && fee.isType) ? this.translate.instant('typeFeeSingular') : this.translate.instant('otherFeeSingular');
    const name = raw || fallback;
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  getFeeTypeLabel(fee: FeeItem): string {
    if ('isTerm' in fee && fee.isTerm) { return this.translate.instant('termFees'); }
    if ('isType' in fee && fee.isType) { return this.translate.instant('typeFeesLabel'); }
    return this.translate.instant('otherFees');
  }

  getTotalFees(fee: FeeItem): number {
    if ('isTerm' in fee && fee.isTerm) { return (fee as TermFee).feesTermAmount || 0; }
    if ('isType' in fee && fee.isType) { return (fee as TypeFee).feesTypeAmount || 0; }
    return (fee as OtherFeeTerm).termAmount || 0;
  }

  getDiscount(fee: FeeItem): number {
    if ('isTerm' in fee && fee.isTerm) { return (fee as TermFee).discount || 0; }
    return (fee as TypeFee | OtherFeeTerm).Discount || 0;
  }

  getDueDate(fee: FeeItem): string | null {
    return fee['DueDate'] ?? fee['termDueDate'] ?? fee['DueData'] ?? null;
  }

  getLateFee(fee: FeeItem): number {
    return fee.lateFee || 0;
  }

  getStatus(fee: FeeItem): FeeStatus {
    if (fee.Balance <= 0) { return 'Paid'; }
    const dueDate = this.getDueDate(fee);
    if (!dueDate) { return 'Pending'; }
    const diffMs = new Date(dueDate).getTime() - Date.now();
    const diffHours = diffMs / (1000 * 60 * 60);
    if (diffHours > 24) { return 'Due'; }
    if (diffHours < 0) { return 'Overdue'; }
    return 'Pending';
  }

  statusChipClass(status: FeeStatus): string {
    return status === 'Paid' ? 'st-chip-green' : 'st-chip-red';
  }

  formatCurrency(amount: number): string {
    return `${this.currencySymbol} ${(amount || 0).toLocaleString('en-IN')}`;
  }

  feesPaidPercent(): number {
    if (!this.feesTotalAmount) { return 0; }
    return Math.round((this.feesTotalPaid / this.feesTotalAmount) * 100);
  }

  // Reached from more than one place (class students list, search) with no
  // shared classId context to reconstruct a specific destination — go back
  // to wherever the user actually came from instead of a hardcoded route.
  goBack() {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/staff/classes']);
    }
  }

  openSibling(sibling: StudentSibling) {
    this.router.navigate(['/staff/student', sibling.id]);
  }

  // ---------- Attendance tab (StudentAttendance) ----------
  async loadAttendanceCalendar(month: Date) {
    const staffDetails = this.staffSession.getStaffDetails();
    const schoolInfo = this.staffSession.getSchoolAndStaffInfo();
    const classData = this.staffSession.getClassData() as any;
    const classId = this.profile?.['classId'] ?? classData?.classId;
    if (!staffDetails || !schoolInfo || !classId || !this.studentId) { return; }

    // Switching months closes any open day drill-down, matching the mobile app.
    this.selectedDay = null;
    this.partialPeriods = [];
    this.calendarMonth = month;
    this.attendanceEntries = [];
    this.attendanceCalendarLoading = true;
    // Build the grid shape immediately (attendanceEntries cleared above) so
    // the card's size never changes when the loading overlay toggles, and
    // no stale colors from the previous month flash while this one loads.
    this.buildCalendarWeeks();
    try {
      const dateParam = `${month.getMonth() + 1}/${month.getFullYear()}`;
      const response = await firstValueFrom(
        this.staffStudentService.getAttendanceCalendar(
          classId, this.studentId, dateParam, schoolInfo.pkAcademicId, schoolInfo.schoolId
        )
      );
      if (response.status && response.body) {
        this.attendanceMonthPercent = Math.round(response.body.attendancePercentage || 0);
        this.attendanceEntries = response.body.attendances || [];
      } else {
        this.attendanceMonthPercent = 0;
        this.attendanceEntries = [];
      }
      this.attendanceCalendarLoaded = true;
    } catch (error) {
      this.attendanceEntries = [];
    } finally {
      this.attendanceCalendarLoading = false;
      this.buildCalendarWeeks();
    }
  }

  changeCalendarMonth(delta: number) {
    const next = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + delta, 1);
    // Mirrors maxDate={today} on the mobile calendar — never scroll past the current month.
    const today = new Date();
    if (next.getFullYear() > today.getFullYear()
      || (next.getFullYear() === today.getFullYear() && next.getMonth() > today.getMonth())) {
      return;
    }
    this.loadAttendanceCalendar(next);
  }

  get calendarMonthLabel(): string {
    return this.calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  private buildCalendarWeeks() {
    const year = this.calendarMonth.getFullYear();
    const month = this.calendarMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay(); // 0 = Sunday
    const gridStart = new Date(year, month, 1 - startOffset);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const statusByDate = new Map<string, AttendanceDayStatus>();
    this.attendanceEntries.forEach(entry => {
      const d = new Date(entry.CreatedDate);
      statusByDate.set(this.dateKey(d), entry.AttendanceStatus);
    });

    const days: CalendarDayTile[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      days.push({
        date,
        dayOfMonth: date.getDate(),
        isCurrentMonth: date.getMonth() === month,
        isFuture: date.getTime() > today.getTime(),
        status: statusByDate.get(this.dateKey(date)) || null,
      });
    }

    this.calendarWeeks = [];
    for (let i = 0; i < days.length; i += 7) {
      this.calendarWeeks.push(days.slice(i, i + 7));
    }
  }

  private dateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  dayTileClass(day: CalendarDayTile): string {
    // Future/out-of-month days are forced white/grey, overriding any status.
    if (!day.isCurrentMonth || day.isFuture) { return 'ssd-cal-day-disabled'; }
    switch (day.status) {
      case 'PRESENT': return 'ssd-cal-day-present';
      case 'ABSENT': return 'ssd-cal-day-absent';
      case 'LATE': return 'ssd-cal-day-late';
      case 'PARTIAL': return 'ssd-cal-day-partial';
      case 'LEAVE': return 'ssd-cal-day-leave';
      default: return 'ssd-cal-day-none';
    }
  }

  isDayTappable(day: CalendarDayTile): boolean {
    return day.isCurrentMonth && !day.isFuture && day.status === 'PARTIAL';
  }

  async selectDay(day: CalendarDayTile) {
    if (!this.isDayTappable(day)) { return; }
    this.selectedDay = day;
    await this.loadPartialAttendance(day.date);
  }

  private async loadPartialAttendance(date: Date) {
    const schoolInfo = this.staffSession.getSchoolAndStaffInfo();
    const classData = this.staffSession.getClassData() as any;
    const classId = this.profile?.['classId'] ?? classData?.classId;
    if (!schoolInfo || !classId || !this.studentId) { return; }

    this.partialLoading = true;
    this.partialPeriods = [];
    try {
      const dateParam = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const response = await firstValueFrom(
        this.staffStudentService.getPartialAttendance(classId, this.studentId, dateParam, schoolInfo.pkAcademicId)
      );
      this.partialPeriods = response.status ? (response.body || []) : [];
    } catch (error) {
      this.partialPeriods = [];
    } finally {
      this.partialLoading = false;
    }
  }

  periodStatusChipClass(status: PartialAttendancePeriod['Attendance']): string {
    switch (status) {
      case 'PRESENT': return 'st-chip-green';
      case 'ABSENT': return 'st-chip-red';
      case 'LATE': return 'st-chip-yellow';
    }
  }

  formatPeriodTime(time: string | null): string | null {
    if (!time) { return null; }
    const [hourStr, minute] = time.split(':');
    const hour = parseInt(hourStr, 10);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${minute} ${period}`;
  }

  // ---------- Documents tab (DocumentsHub) ----------
  async loadDocuments() {
    const staffDetails = this.staffSession.getStaffDetails();
    const classData = this.staffSession.getClassData() as any;
    const classId = this.profile?.['classId'] ?? classData?.classId;
    if (!staffDetails || !classId || !this.studentId) {
      this.documentsLoadError = true;
      return;
    }

    this.documentsLoading = true;
    this.documentsLoadError = false;
    try {
      const response = await firstValueFrom(
        this.staffStudentService.getUploadedDocuments(classId, this.studentId, staffDetails.staffId)
      );
      const list = response.status ? (response.body?.uploadedDocumentsList || []) : [];
      this.allDocuments = list;
      this.applyDocumentSearch();
      this.documentsLoaded = true;
    } catch (error) {
      this.documentsLoadError = true;
    } finally {
      this.documentsLoading = false;
    }
  }

  onDocumentSearchChange() {
    this.applyDocumentSearch();
  }

  private applyDocumentSearch() {
    const query = this.documentSearchQuery.trim().toLowerCase();
    this.documents = this.allDocuments.filter(d => d.documentTitle.toLowerCase().includes(query));
  }

  documentIcon(doc: UploadedDocument): string {
    switch (doc.fileType) {
      case 'pdf': return 'ri-file-pdf-line';
      case 'xls':
      case 'xlsx': return 'ri-file-excel-line';
      default: return 'ri-image-line';
    }
  }

  openDocument(doc: UploadedDocument) {
    if (doc.fileType === 'xls' || doc.fileType === 'xlsx') {
      this.downloadDocument(doc);
      return;
    }
    this.previewDocument = doc;
    this.previewSafeUrl = doc.fileType === 'pdf'
      ? this.sanitizer.bypassSecurityTrustResourceUrl(doc.documentUrl)
      : null;
    this.showDocumentPreview = true;
  }

  closeDocumentPreview() {
    this.showDocumentPreview = false;
    this.previewDocument = null;
    this.previewSafeUrl = null;
  }

  downloadDocument(doc: UploadedDocument) {
    const link = document.createElement('a');
    link.href = doc.documentUrl;
    link.download = `${this.profile?.studentName || 'student'} ${doc.documentTitle}`;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

}
